<script setup lang="ts">
  import { computed } from "vue";
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

  type CollapseKey = number | string;

  interface Props {
    collapseName: CollapseKey;
    visibleCollapse?: CollapseKey | CollapseKey[];
    spanTopLeft?: number;
    spanTopCenter?: number;
    spanTopRight?: number;
    spanBottomLeft?: number;
    spanBottomCenter?: number;
    spanBottomRight?: number;
    disabled?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    spanTopLeft: 1,
    spanTopCenter: 8,
    spanTopRight: 3,
    spanBottomLeft: 1,
    spanBottomCenter: 8,
    spanBottomRight: 3,
    visibleCollapse: -1,
    disabled: false,
  });

  const emit = defineEmits<{
    toggle: [name: CollapseKey];
  }>();

  const isOpen = computed({
    get: () =>
      Array.isArray(props.visibleCollapse)
        ? props.visibleCollapse.includes(props.collapseName)
        : props.collapseName === props.visibleCollapse,
    set: (value: boolean) => {
      if (Array.isArray(props.visibleCollapse)) {
        emit("toggle", props.collapseName);
      } else if (value) {
        emit("toggle", props.collapseName);
      } else {
        emit("toggle", -1);
      }
    },
  });

  const handleOpenChange = (value: boolean) => (isOpen.value = value);
  const getSpanStyle = (span: number) => {
    if (!span) return { display: "none" };
    return { gridColumn: `span ${span} / span ${span}` };
  };
</script>

<template>
  <Collapsible
    :open="isOpen"
    :disabled="disabled"
    class="group/collapsible mb-2 w-full rounded-lg bg-white shadow-sm last:mb-0"
    :class="{ 'opacity-50 pointer-events-none': disabled }"
    @update:open="handleOpenChange">
    <CollapsibleTrigger
      as-child
      :disabled="disabled">
      <div
        class="grid h-14 w-full cursor-pointer grid-cols-12 items-center px-6"
        :class="{ 'cursor-not-allowed': disabled }">
        <div
          class="flex items-center"
          :style="getSpanStyle(spanTopLeft)">
          <slot name="top-left" />
        </div>
        <div
          class="flex items-center"
          :style="getSpanStyle(spanTopCenter)">
          <slot name="top-center" />
        </div>
        <div
          class="flex items-center"
          :style="getSpanStyle(spanTopRight)">
          <slot name="top-right" />
        </div>
      </div>
    </CollapsibleTrigger>
    <CollapsibleContent class="px-6 py-4 data-[state=closed]:animate-none data-[state=open]:animate-none">
      <div class="grid w-full grid-cols-12 items-center">
        <div
          class="flex items-center"
          :style="getSpanStyle(spanBottomLeft)">
          <slot name="bottom-left" />
        </div>
        <div
          class="flex items-center"
          :style="getSpanStyle(spanBottomCenter)">
          <slot name="bottom-center" />
        </div>
        <div
          class="flex items-center"
          :style="getSpanStyle(spanBottomRight)">
          <slot name="bottom-right" />
        </div>
      </div>
    </CollapsibleContent>
  </Collapsible>
</template>
